import React, { useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody } from '@this/components/shared/ui/feedbacks/modal';
import { Link } from '@this/components/shared/ui/navigations/link';
import Box from '@material-ui/core/Box';
import { Button } from '@this/components/shared/ui/inputs/button';
import { Checkbox } from '@this/components/shared/ui/inputs/checkbox';
import { Fetcher } from '@this/src/util';
import OrderItemCalendarEvent, {
  convertOrderItemCalendarEventResponseToArgs
} from '@this/src/domain/order_item_calendar_event';
import type { OrderItemCalendarEventResponseArgs } from '@this/src/domain/order_item_calendar_event';

type CalendarProvider = 'google' | 'outlook';

interface CalendarSuccessModalProps {
  provider: CalendarProvider;
  trip: any;
  onEventRegistered?: (traceId: string, calendarEvent: OrderItemCalendarEvent) => void;
}

interface ProviderConfig {
  urlParam: string;
  apiParam: string;
  modalTitle: string;
}

const CalendarSuccessModal: React.FC<CalendarSuccessModalProps> = ({ provider, trip, onEventRegistered }) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [registeredCalendarEvent, setRegisteredCalendarEvent] = useState<OrderItemCalendarEvent | null>(null);
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(false);

  const config: ProviderConfig = {
    google: {
      urlParam: 'google_calendar_connected',
      apiParam: 'google',
      modalTitle: 'Googleカレンダーの連携が完了しました'
    },
    outlook: {
      urlParam: 'outlook_calendar_connected',
      apiParam: 'outlook',
      modalTitle: 'Outlookカレンダーの連携が完了しました'
    }
  }[provider];

  const searchParams = new URLSearchParams(window.location.search);
  const calendarConnected = searchParams.get(config.urlParam);
  const traceId = searchParams.get('trace_id');

  useEffect(() => {
    if (calendarConnected && traceId) {
      setOpen(true);
      setIsLoading(true);
      setMessage(null);
      setRegisteredCalendarEvent(null);
      handleRegisterCalendarEvent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarConnected, traceId]);

  const handleRegisterCalendarEvent = async () => {
    if (!trip || !trip.id) {
      setIsLoading(false);
      setMessage('エラー: 旅程情報が見つかりません。');
      return;
    }
    try {
      history.pushState(null, '', window.location.pathname);
      const response = await Fetcher.post<OrderItemCalendarEventResponseArgs>(
        `/trips/${trip.id}/calendar_events.json`,
        {
          trace_id: traceId,
          calendar: config.apiParam
        }
      );
      setIsLoading(false);
      setMessage('カレンダーに予定を登録しました。');
      setRegisteredCalendarEvent(
        new OrderItemCalendarEvent(convertOrderItemCalendarEventResponseToArgs(response))
      );

      if (onEventRegistered && traceId) {
        onEventRegistered(
          traceId,
          new OrderItemCalendarEvent(convertOrderItemCalendarEventResponseToArgs(response))
        );
      }

      // カレンダー連携時は自動登録オンにする
      toggleAutoSync();
    } catch (error: any) {
      console.error(`[${provider}] Calendar registration failed:`, error);

      if (error.response?.status === 400) {
        setMessage('カレンダーの連携が解除されています。再度連携してください。');
      } else {
        const errorMessage =
          error.response?.data?.error || 'カレンダーへの予定登録に失敗しました。時間をおいて再度お試しください。';
        setMessage(errorMessage);
      }
      setIsLoading(false);
    }
  };

  const toggleAutoSync = async () => {
    try {
      setAutoSyncEnabled(!autoSyncEnabled);
      await Fetcher.put('/users/update.json', {
        auto_sync_trip_to_calendar: !autoSyncEnabled
      });
    } catch (error) {
      console.error('Auto register calendar event failed:', error);
    }
  };

  return (
    <Modal open={open} onClose={() => setOpen(false)}>
      <ModalHeader>{config.modalTitle}</ModalHeader>
      <ModalBody>
        {isLoading ? (
          <p>カレンダーに予定を登録しています...</p>
        ) : (
          <>
            {message && <p>{message}</p>}
            {registeredCalendarEvent && (
              <p>
                <Link href={registeredCalendarEvent.htmlLink} target="_blank" rel="noopener noreferrer">
                  登録した予定を見る
                </Link>
              </p>
            )}
            <Box mt={3}>
              <Checkbox onChange={toggleAutoSync} checked={autoSyncEnabled}>
                新しい予定は自動でカレンダーに登録する
              </Checkbox>
              <Box>
                ※
                <Link href="/users/edit" target="_blank" rel="noopener noreferrer">
                  アカウント設定
                </Link>
                からいつでも変更できます
              </Box>
            </Box>
          </>
        )}
        <Box mt={3} display="flex" justifyContent="flex-end">
          <Button onClick={() => setOpen(false)}>閉じる</Button>
        </Box>
      </ModalBody>
    </Modal>
  );
};

export default CalendarSuccessModal;
